import assert from 'browser-assert';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';

import { InvisibleSmartCaptcha } from '@yandex/smart-captcha';

import Plate from 'components/UI/Plate/Plate';
import Button from 'components/UI/Button/Button';
import { TextInput } from 'components/UI/TextInput/TextInput';
import CheckBox from 'components/UI/CheckBox/CheckBox.jsx';

import AppStore from 'AppStore.js';
import messageBoxStore from 'MessageBox/MessageBoxStore';
import popupStore from 'components/Popup/PopupStore.js';

import { ApiError } from '../../client.js';
import { getCookie, setCookie } from 'utils/cookie.js';

import { SponsorId } from 'domain/id.js';

import { EVENTS, metricsEvent } from 'metrics';

import client from 'client';

import './SignUp.scss';

const SMART_CAPTCHA_KEY = process.env.REACT_APP_SMART_CAPTCHA_KEY;
const SMART_CAPTCHA_TEST = !!process.env.REACT_APP_SMART_CAPTCHA_TEST;

export const SignUp = observer(({ sponsorId }) => {
  assert(!sponsorId || (sponsorId instanceof SponsorId));

  const navigate = useNavigate(); 
  const redirectUrl = getCookie('redirect_url');

  const [isBusy, setIsBusy] = useState(false);

  const [login, setLogin] = useState('');
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');

  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaVisible, setCaptchaVisible] = useState(false);

  async function onSubmit() {
    if (isBusy || captchaVisible)
      return;

    if (!login)
      return messageBoxStore.showError('Введите email');

    if (!password1)
      return messageBoxStore.showError('Введите пароль');
    if (password2 !== password1)
      return messageBoxStore.showError('Пароли не совпадают');

    if (login.trim().toLowerCase() === password1.trim().toLowerCase())
      return messageBoxStore.showError('Пароль и логин не должны совпадать');

    if (SMART_CAPTCHA_KEY && !captchaToken)
      return setCaptchaVisible(true);
    onCaptchaSuccess(captchaToken);
  }

  async function onCaptchaSuccess(captchaToken) {
    setCaptchaVisible(false);
    setCaptchaToken(captchaToken);

    let token = '';

    setIsBusy(true);
    try {
      const regdata = {
        login, password: password1
      };
      if (captchaToken)
        regdata.captcha = captchaToken;
      if (sponsorId)
        regdata.sponsorId = sponsorId.valueOf();

      token = await client.signup(regdata);
      metricsEvent(EVENTS.SIGNUP_SUBMIT);
    } catch (e) {
      if (e instanceof ApiError) {
        messageBoxStore.showError(e.message);
        return;
      }
      throw e;
    } finally {
      setIsBusy(false);
    }

    AppStore.setToken(token);
    AppStore.setRightNowRegistered();
    
    await AppStore.updateAcceptAgreements();
    
    if (redirectUrl) {
      setCookie('redirect_url', '', { expires: -1 });
      navigate(redirectUrl);
    } else {
      navigate('/');
    }
  }

  const handleCheckboxClick = () => {
    if (AppStore.agreedWithRules) {
      AppStore.setAgreedWithRules(false);
    } else {
      popupStore.open('accept-rules');
    }
  };

  return (
    <div className='signup_page'>
      <Helmet>
        <title>Регистрация в сервисе — Boostclick</title>
        <meta name="description" content="Регистрация для доступа к сервису Boostclick" />
        <meta name="robots" content="noindex" />
        <link rel="canonical" href={window.location.origin + window.location.pathname} />
      </Helmet>

      <div className='signup'>
        <form className="signup__form" onSubmit={e => { e.preventDefault(); onSubmit() }}>
          <Plate className='signup__plate'>
            <h1 className='signup__title'>Регистрация</h1>

            <TextInput classes={["signup__input"]} placeholder='Email' type='email' onChange={e => setLogin(e.target.value)} />
            <TextInput classes={["signup__input"]} placeholder='Пароль' type='password' onChange={e => setPassword1(e.target.value)} />
            <TextInput classes={["signup__input"]} placeholder='Повторите пароль' type='password' onChange={e => setPassword2(e.target.value)} />

            <CheckBox
              title="Согласен(-a) с правилами и условиями использования сайта"
              classes={["signup__check"]}
              checked={AppStore.agreedWithRules}
              onClick={handleCheckboxClick}
            />

            <Button title='Зарегистрироваться' disabled={!AppStore.agreedWithRules} />
          </Plate>
        </form>
      </div>

      {SMART_CAPTCHA_KEY && <InvisibleSmartCaptcha
        sitekey={SMART_CAPTCHA_KEY}
        test={SMART_CAPTCHA_TEST}
        visible={captchaVisible}
        hideShield={true}
        onSuccess={onCaptchaSuccess}
        onTokenExpired={() => setCaptchaToken('')}
        onNetworkError={() => setCaptchaVisible(false)}
        onChallengeHidden={() => setCaptchaVisible(false)}
      />}

    </div>
  );
});
